import { useLocalStorage } from '@uidotdev/usehooks';
import { useLayoutEffect, useState } from 'react';

export const COLUMN_DEFAULT_WIDTH = 440;
export const COLUMN_MIN_WIDTH = 360;
/** The narrowest the invoice area gets beside the docked column. */
export const INVOICE_AREA_MIN_WIDTH = 1110;
/** The new design keeps its side panels' gap between the invoice area and the column. */
export const NEW_DESIGN_GAP = 24;
const WINDOW_SHARE = 0.4;

export const dockedKey = (employeeId: number | undefined) => `invoice-comments-docked-${employeeId ?? ''}`;
export const widthKey = (employeeId: number | undefined) => `invoice-comments-width-${employeeId ?? ''}`;

/**
 * Whether the user docks the comments, and the column's width they last set, both kept per employee in
 * localStorage. The width is the one remembered: a column drawn narrower for want of room keeps it.
 */
export function useCommentsDock(employeeId: number | undefined) {
    const [docked, rememberDocked] = useLocalStorage(dockedKey(employeeId), false);
    const [width, rememberWidth] = useLocalStorage(widthKey(employeeId), COLUMN_DEFAULT_WIDTH);
    return { docked, rememberDocked, width, rememberWidth };
}

/**
 * The room for the column in a row `rowWidth` wide: whether a column at its minimum fits beside the narrowest
 * invoice area, and the widest it may be, the smaller of 40% of the window and what the invoice area leaves.
 */
export function commentsRoom(rowWidth: number, windowWidth: number, isNewDesign: boolean) {
    const space = rowWidth - (isNewDesign ? NEW_DESIGN_GAP : 0) - INVOICE_AREA_MIN_WIDTH;
    return {
        hasRoom: space >= COLUMN_MIN_WIDTH,
        maxWidth: Math.max(COLUMN_MIN_WIDTH, Math.min(windowWidth * WINDOW_SHARE, space)),
    };
}

/**
 * The width of the element the returned callback ref is set on, read again on every resize of it, before paint;
 * 0 while it is not mounted. A callback ref, as the element mounts after the page's first render. Not `useMeasure`,
 * which sets the width only from the observer's callback: a frame after the row first paints at its full width.
 */
export function useElementWidth<T extends HTMLElement>(): [number, (node: T | null) => void] {
    const [node, setNode] = useState<T | null>(null);
    const [width, setWidth] = useState(0);
    useLayoutEffect(() => {
        if (!node) return undefined;
        const measure = () => setWidth(node.getBoundingClientRect().width);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, [node]);
    return [node ? width : 0, setNode];
}
